/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import { FixedSizeList as List } from 'react-window';
import { AutoSizer } from 'react-virtualized';

import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { calendarIcons } from '../../../../shared/constants';
import {
  setActiveModal,
  addPreSavedItemToCalendarItemsArray,
  addPreSavedItemToProgramItemsArray,
} from '../../ducks/calendarActions';

/**
 * Full-screen exercise picker for phones. Replaces the react-select dropdown
 * used on desktop (SelectDropdownExercisePicker), whose portaled popup covers
 * the search input / floats off-screen on small viewports. Rendered only on
 * phones by ExercisePicker; desktop is untouched.
 *
 * Self-contained on purpose: it reads the same Redux data and reproduces the
 * same add + create-exercise dispatches as the dropdown, so no shared
 * (desktop-affecting) code is refactored.
 */

const Overlay = styled('div')`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: white;
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #EEEEEE;
`;

const SearchAndIcon = styled('div')`
  display: flex;
  align-items: center;
  flex: 1;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  min-height: 40px;
  padding: 0 10px;
  gap: 8px;
`;

const SearchInput = styled('input')`
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: #444444;
  background: transparent;

  ::placeholder {
    color: #9E9E9E;
  }
`;

const DoneButton = styled('button')`
  border: none;
  background: none;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  padding: 8px;
  cursor: pointer;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px #77A7FF;
    border-radius: 4px;
  }
`;

const ListBody = styled('div')`
  flex: 1;
  /* min-height:0 lets this flex child actually shrink so the footer below it
     is never pushed off-screen (default min-height:auto would clip it) */
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
`;

const Row = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;

  &:active {
    background: #F4F4F4;
  }
`;

const RowLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const IconContainer = styled('div')`
  height: 28px;
  width: 28px;
  min-width: 28px;
  border-radius: ${(props) => (props.square ? '4px' : '50%')};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  ${(props) => props.backgroundColor && `background: ${props.backgroundColor};`}
  ${(props) => props.backgroundColor && `border: 1px solid ${props.backgroundColor};`}
`;

const RowName = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: #444444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #9E9E9E;
  text-align: center;
  padding: 24px 16px;
`;

const Footer = styled('div')`
  border-top: 1px solid #EEEEEE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 12px;
  flex-shrink: 0;
`;

const CreateCta = styled('button')`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: none;
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: #444444;
  cursor: pointer;
  padding: 4px 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px #77A7FF;
    border-radius: 4px;
  }
`;

const CreateLabel = styled('span')`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* matches the dropdown footer's create-icon box (bordered white square) */
const CreateIconBox = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;
  background-color: white;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  flex-shrink: 0;
`;

const QuickIconRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const QuickIconBox = styled('button')`
  height: 32px;
  width: 32px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${(props) => props.backgroundColor || 'transparent'};
`;

const iconMetaForType = (type) => Object.values(calendarIcons)
  .find((iconObj) => iconObj.type === type);

const MobileAddExercisePicker = ({
  isOpen, onClose, date, dayNumber,
}) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const allExercises = useSelector((state) => state.calendar.data.allExercises);
  const tagsForAccount = useSelector((state) => state.calendar.data.tagsForAccount);
  const accessoryItems = useSelector(
    (state) => state.calendar.data.calendarItemContainers.accessoryCalendarItems,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const hasExerciseManagementAccess = currentUser?.userSettings?.exercises === 1;

  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');

  /** Same option set as the add-mode dropdown: exercises + tags + accessory
   * quick-adds (rest, video, session break). Mirrors
   * SelectDropdownExercisePicker's exerciseOptions/tagOptions/accessory. */
  const options = useMemo(() => {
    const tagOptions = tagsForAccount.map((tag) => ({
      ...tag,
      name: tag.name,
      exerciseId: tag.id,
      icon: 'tag2',
      iconColor: '#FF4500',
      squareIconContainer: true,
      iconBackgroundColor: 'rgba(255, 69, 0, 0.2)',
    }));
    const accessoryOptions = accessoryItems.map((exercise) => ({
      ...exercise,
      name: exercise.name,
      exerciseId: exercise.id,
      squareIconContainer: true,
      iconBackgroundColor: 'white',
    }));
    const exerciseOptions = allExercises.map((exercise) => {
      const meta = iconMetaForType(exercise.type);
      return {
        ...exercise,
        name: exercise.name,
        exerciseId: exercise.id,
        icon: meta?.icon,
        iconColor: meta?.iconColor,
        squareIconContainer: true,
        iconBackgroundColor: meta?.containerColor,
      };
    });
    return [...exerciseOptions, ...tagOptions, ...accessoryOptions];
  }, [allExercises, tagsForAccount, accessoryItems]);

  /** Search: word-includes filter + relevance ranking, priority types on top.
   * Ports the dropdown's manual search (SelectDropdownExercisePicker ~450-491)
   * so results match what desktop users see. */
  const results = useMemo(() => {
    const normalize = (str) => (str || '').toLowerCase().trim();
    const query = normalize(searchTerm);
    const searchWords = query.split(/\s+/).filter(Boolean);

    const filtered = options.filter((obj) => {
      const name = normalize(obj.name);
      return searchWords.every((word) => name.includes(word));
    });

    const priorityTypes = ['video', 'session_break', 'rest'];
    const priorityItems = filtered.filter((item) => priorityTypes.includes(item.type));
    const normalItems = filtered.filter((item) => !priorityTypes.includes(item.type));

    const getRelevanceScore = (name) => {
      if (!query) return 0;
      if (name === query) return 0;
      if (name.startsWith(query)) return 1;
      if (name.split(/\s+/).some((word) => word.startsWith(query))) return 2;
      if (name.includes(query)) return 3;
      return 4;
    };

    const sortedNormalItems = query
      ? [...normalItems].sort((a, b) => {
        const nameA = normalize(a.name);
        const nameB = normalize(b.name);
        const scoreA = getRelevanceScore(nameA);
        const scoreB = getRelevanceScore(nameB);
        if (scoreA !== scoreB) return scoreA - scoreB;
        return nameA.localeCompare(nameB);
      })
      : normalItems;

    return [...priorityItems, ...sortedNormalItems];
  }, [options, searchTerm]);

  /** Identical to SelectDropdownExercisePicker.handleAddExercise. Keeps the
   * picker open so several items can be added in one session. */
  const handleAddExercise = (option) => {
    const newItem = {
      ...option,
      type: option.type || 'T',
      icon: option.icon,
      title: option.name,
      exerciseId: parseInt(option.id, 10),
      id: `${option.id}-${generateUniqueId()}`,
      assignedId: `${option.id}-${generateUniqueId()}`,
      preSave: true,
    };
    if (programType === 'free-form-program') {
      dispatch(addPreSavedItemToProgramItemsArray(newItem, dayNumber || selectedProgramDayNumber));
    } else {
      dispatch(addPreSavedItemToCalendarItemsArray(newItem, endpointFormattedDate));
    }
  };

  const openCreateModal = (type) => {
    dispatch(setActiveModal('createNewComponentModal', {
      type,
      openedFromExercisePicker: true,
      targetDate: endpointFormattedDate,
    }));
    onClose();
  };

  if (!isOpen) return null;

  const hasResults = results.length > 0;

  return (
    <Overlay>
      <Header>
        <SearchAndIcon>
          <IcomoonReact iconSet={iconSet} size={16} icon='magnifying-glass-alt' color='#9E9E9E' />
          {/* intentionally not auto-focused: popping the keyboard on open
              covers the bottom footer (the Create button) */}
          <SearchInput
            type='text'
            placeholder='Search Exercises'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchAndIcon>
        <DoneButton type='button' onClick={onClose}>Done</DoneButton>
      </Header>

      <ListBody>
        {hasResults ? (
          /* virtualized so opening the picker doesn't mount thousands of rows
             (each with an inline SVG icon) at once - that was the ~1-2s lag */
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                width={width}
                itemCount={results.length}
                itemSize={52}
              >
                {({ index, style }) => {
                  const option = results[index];
                  return (
                    <Row
                      style={style}
                      key={option.assignedId || `${option.exerciseId}-${option.type}`}
                      onClick={() => handleAddExercise(option)}
                    >
                      <RowLeft>
                        <IconContainer
                          square={option.squareIconContainer}
                          backgroundColor={option.iconBackgroundColor}
                        >
                          <IcomoonReact
                            iconSet={iconSet}
                            size={16}
                            icon={option.icon}
                            color={option.iconColor || '#444444'}
                          />
                        </IconContainer>
                        <RowName>{option.name}</RowName>
                      </RowLeft>
                      {option.media?.length ? (
                        <IcomoonReact iconSet={iconSet} size={16} icon='video-camera' color='#9E9E9E' />
                      ) : null}
                    </Row>
                  );
                }}
              </List>
            )}
          </AutoSizer>
        ) : (
          <EmptyState>{`No exercises match "${searchTerm}"`}</EmptyState>
        )}
      </ListBody>

      <Footer>
        <CreateCta
          type='button'
          onClick={() => openCreateModal(
            !hasExerciseManagementAccess ? 'N' : undefined,
          )}
        >
          <CreateIconBox>
            <IcomoonReact iconSet={iconSet} size={10} icon='plus' color='#9E9E9E' />
          </CreateIconBox>
          {!hasResults && !!searchTerm ? (
            <CreateLabel>{`Add "${searchTerm}..."`}</CreateLabel>
          ) : (
            <CreateLabel>Create New Exercise</CreateLabel>
          )}
        </CreateCta>
        <QuickIconRow>
          <QuickIconBox
            type='button'
            backgroundColor='rgba(249, 191, 59, 0.2)'
            onClick={() => openCreateModal('N')}
          >
            <IcomoonReact iconSet={iconSet} size={15} icon='note' color='#F9BF3B' />
          </QuickIconBox>
          <QuickIconBox
            type='button'
            backgroundColor='rgba(231, 76, 60, 0.2)'
            onClick={() => openCreateModal('W')}
          >
            <IcomoonReact iconSet={iconSet} size={15} icon='warmup' color='#E74C3C' />
          </QuickIconBox>
        </QuickIconRow>
      </Footer>
    </Overlay>
  );
};

MobileAddExercisePicker.defaultProps = {
  date: null,
  dayNumber: null,
};

MobileAddExercisePicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.number,
};

export default MobileAddExercisePicker;
